import type { NoteActivity, NoteActivityVersion, User } from '@paintscout/api'
import { EstimatorAvatar, Typography, useDialogs, useUsers } from '../../'
import { addAuth0Prefix, getActivityColor } from '@paintscout/util'
import ViewVersionDialog from '../../dialogs/ViewVersionDialog'
import type { Theme } from '@material-ui/core/styles'
import type { StyleClasses } from '../../styles'
import { makeStyles } from '@material-ui/core'
import moment from 'moment'
import React from 'react'

const useStyles = makeStyles<Theme, NoteHistoryPopoverProps>(
  (theme) => ({
    root: {},
    historyPopover: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'flex-start'
    },
    popoverHeader: {
      padding: theme.spacing(1.25),
      paddingBottom: theme.spacing(0.5),
      paddingTop: theme.spacing(1),
      borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
      width: '100%'
    },
    popoverList: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      padding: theme.spacing(0.5),
      paddingLeft: 0
    },
    popoverItem: {
      minWidth: '200px',
      width: '100%',
      gap: theme.spacing(1),
      padding: theme.spacing(1),
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderRadius: theme.borderRadius.lg,
      '&:hover': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P94,
        cursor: 'pointer'
      }
    },
    itemName: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing(1)
    },
    dropdownAvatar: {}
  }),
  { name: 'NoteHistoryPopover' }
)

export interface NoteHistoryPopoverProps {
  classes?: StyleClasses<typeof useStyles>
  note: NoteActivity
  totalEdits: number
  onNoteClick: () => void
}

function NoteHistoryPopover(props: NoteHistoryPopoverProps) {
  const classes = useStyles(props)
  const { note, totalEdits, onNoteClick } = props
  const { users } = useUsers()
  const { openDialog, dismissDialog } = useDialogs()

  const userMap = Object.values(users).reduce(
    (acc, user) => {
      acc[user.user_id] = user
      return acc
    },
    {} as { [key: string]: User }
  )

  function handleVersionClick(version: NoteActivityVersion) {
    onNoteClick()
    openDialog(ViewVersionDialog, {
      original: note?.details?.versions[totalEdits - 1].timestamp === version.timestamp,
      version,
      user: userMap[addAuth0Prefix(version.owner.id)],
      onCancel: dismissDialog
    })
  }

  return (
    <div className={classes.historyPopover}>
      <div className={classes.popoverHeader}>
        <Typography variant="h6" style={{ textTransform: 'none' }}>
          Version History
        </Typography>
      </div>
      <div className={classes.popoverList}>
        {note?.details?.versions?.map((version, i) => {
          const matchingUser = userMap[addAuth0Prefix(version.owner.id)]
          return (
            <div className={classes.popoverItem} key={i} onClick={() => handleVersionClick(version)}>
              <div className={classes.itemName}>
                <EstimatorAvatar
                  height="20px"
                  width="20px"
                  fontSize="10px"
                  owner={version.owner}
                  className={classes.dropdownAvatar}
                  userPictures={{
                    [addAuth0Prefix(version.owner.id)]: matchingUser?.user_metadata?.customAvatar?.cdnValue ?? ''
                  }}
                  avatarColor={getActivityColor(matchingUser?.user_metadata?.color)}
                  style={{
                    paddingTop: 0
                  }}
                  hideTooltip
                />
                <Typography variant="body2" weight={'bold'}>
                  {userMap[addAuth0Prefix(version.owner.id)].name}
                </Typography>
              </div>
              <Typography variant="body2">{moment(version.timestamp).fromNow()}</Typography>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default NoteHistoryPopover
